/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment, useState, type FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { EuiBasicTable, EuiSkeletonRectangle } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { LineItems } from '@modules/billing-api/customTypes'
import useGetLineItems from '@modules/billing-lib/queries/useGetLineItems'
import { CuiAlert } from '@modules/cui/Alert'
import { getActiveBalance } from '@modules/billing-lib/utils'

import notificationMessages from '../../messages'

import useColumns from './useColumns'
import AlertDetails from './AlertDetails'

import type { Alert } from './types'

const DEFAULT_ID = 'credit-consumption-alert-default-id'
const ALERT_STATUS_ACTIVE = true
const TRIGGER_THRESHOLDS = ['33%', '25%', '16%']
const SCOPE = 'Organization'

type Props = {
  organizationId: string
}

const AlertsTable: FunctionComponent<Props> = ({ organizationId }) => {
  const [isAlertsDetailsOpen, setIsAlertDetailsOpen] = useState(false)
  const [currentAlert, setCurrentAlert] = useState<Alert | null>(null)
  const { formatMessage } = useIntl()

  const handleViewAlertDetails = (alert: Alert) => {
    setCurrentAlert(alert)
    setIsAlertDetailsOpen(true)
  }

  const columns = useColumns({
    onViewAlert: handleViewAlertDetails,
  })

  const handleCloseAlertDetails = () => {
    setIsAlertDetailsOpen(false)
    setCurrentAlert(null)
  }

  const { data, isFetching, isError } = useGetLineItems({
    pathParameters: { organization_id: organizationId },
  })

  if (isError) {
    return (
      <CuiAlert size='s' type='error' data-test-id='error-message'>
        <FormattedMessage {...notificationMessages.genericErrorMessage} />
      </CuiAlert>
    )
  }

  if (isFetching) {
    return (
      <EuiSkeletonRectangle
        width='100%'
        data-test-subj='billing.notifications.prepaid-consumption.table.loading'
      />
    )
  }

  const { available, remaining } = getActiveBalance(data as LineItems)

  const getCreditConsumptionAlerts = () => [
    {
      id: DEFAULT_ID,
      active: ALERT_STATUS_ACTIVE,
      triggers: TRIGGER_THRESHOLDS,
      scope: SCOPE,
      balance: {
        remaining,
        total: available,
      },
    },
  ]

  return (
    <Fragment>
      <EuiBasicTable
        items={getCreditConsumptionAlerts()}
        columns={columns}
        tableCaption={formatMessage({
          id: 'billing.notifications.budgets.table.caption',
          defaultMessage: 'Budgets table',
        })}
      />

      {isAlertsDetailsOpen && currentAlert && (
        <AlertDetails alert={currentAlert} onClose={handleCloseAlertDetails} />
      )}
    </Fragment>
  )
}

export default AlertsTable
